/**
 * Slovak (sk) translations — the primary product locale.
 *
 * This is the source-of-truth dictionary. An English ('en') file with the same
 * key shape can be added later and selected by `useI18n`. Keys are dotted and
 * grouped by surface; keep them flat-by-namespace for easy diffing.
 *
 * DUAL-MODE COPY: Solandio is an intact-family app by default ('family' mode);
 * the co-parenting machinery sleeps behind `family.mode === 'separated'`. Where
 * a surface reads differently per mode, this dictionary ships BOTH variants
 * (e.g. `messages.titleFamily` / `messages.titleSeparated`) and the UI picks the
 * right one by mode. The bare key (e.g. `messages.title`) is the family-mode
 * default, so legacy callers that haven't been mode-aware'd yet read warm copy.
 *
 * TONE in family mode: warm, plain Slovak, second person, concrete placeholder
 * examples. ZERO legal vocabulary — `súd`, `striedavka`, `odovzdávka`, `dohoda`,
 * `50/50` appear ONLY in the explicit `*Separated` strings.
 */
export const sk = {
  appName: 'Solandio',
  tagline: 'Slnečná zóna pre vaše deti a rodinu.',

  language: {
    label: 'Jazyk',
    select: 'Vybrať jazyk',
  },

  common: {
    save: 'Uložiť',
    saving: 'Ukladám…',
    cancel: 'Zrušiť',
    add: 'Pridať',
    edit: 'Upraviť',
    delete: 'Vymazať',
    remove: 'Odstrániť',
    close: 'Zavrieť',
    loading: 'Načítavam…',
    none: 'Žiadne',
    optional: 'nepovinné',
    back: 'Späť',
    today: 'Dnes',
    note: 'Poznámka',
    required: 'Toto pole je povinné.',
    error: 'Niečo sa pokazilo.',
    retry: 'Skúsiť znova',
    confirmDelete: 'Naozaj odstrániť?',
    skipToContent: 'Preskočiť na obsah',
  },

  auth: {
    signInTitle: 'Vitajte v Solandio',
    signInLead:
      'Pokojné, prehľadné miesto, kde má celá rodina poriadok — kalendár, deti, nákupy aj náklady.',
    signInMicrosoft: 'Prihlásiť sa alebo zaregistrovať sa',
    signInGoogle: 'Prihlásiť sa cez Google',
    signInMock: 'Prihlásiť sa (lokálny vývoj)',
    signOut: 'Odhlásiť sa',
    signedInAs: 'Prihlásený/á ako',
    loadingSession: 'Overujem prihlásenie…',
    warmingUp:
      'Pripravujeme Solandio… pri prvom otvorení po dlhšom čase to môže pár sekúnd trvať.',
    devNotice:
      'Lokálny vývojový režim — bez Azure. Dáta sú uložené len vo vašom prehliadači.',
  },

  nav: {
    home: 'Domov',
    children: 'Deti',
    contacts: 'Kontakty',
    health: 'Zdravie',
    calendar: 'Kalendár',
    shopping: 'Nákupy',
    documents: 'Dokumenty',
    messages: 'Odkazy',
    expenses: 'Náklady',
    agreements: 'Dohody',
    sharing: 'Rodina',
    audit: 'Audit',
    settings: 'Nastavenia',
    modules: 'Moduly',
    menu: 'Ponuka',
  },

  settings: {
    title: 'Nastavenia',
    lead: 'Spravujte svoj profil, jazyk a prihlásenie.',
    profile: 'Profil',
    displayName: 'Zobrazované meno',
    displayNameHint: 'Toto meno vidia ostatní členovia rodiny.',
    email: 'E-mail',
    emailHint: 'E-mail sa nedá zmeniť — je naviazaný na vaše prihlásenie.',
    nameInvalid: 'Zadajte meno (1 – 120 znakov).',
    saved: 'Uložené.',
    preferences: 'Predvoľby',
    account: 'Účet',
    signOutHint: 'Odhláste sa z tohto zariadenia.',
  },

  profileSetup: {
    title: 'Vitajte v Solandio!',
    lead: 'Než začneme, povedzte nám, ako vás máme volať.',
    nameLabel: 'Ako vás máme volať?',
    nameHint: 'Toto meno uvidia ostatní členovia rodiny. Môžete ho kedykoľvek zmeniť v Nastaveniach.',
    namePlaceholder: 'napr. Jana',
    nameInvalid: 'Zadajte meno (1 – 120 znakov).',
    submit: 'Pokračovať',
  },

  family: {
    switcher: 'Rodina',
    create: 'Vytvoriť rodinu',
    createTitle: 'Vytvorte si rodinu',
    createLead:
      'Rodina je váš spoločný priestor — pre partnera, deti aj starých rodičov. Vy rozhodujete, kto čo vidí.',
    nameLabel: 'Názov rodiny',
    namePlaceholder: 'napr. Naša rodina',
    empty: 'Zatiaľ nemáte žiadnu rodinu.',
  },

  home: {
    /** Greeting; {name} is the signed-in member's first name. */
    greetingMorning: 'Dobré ráno, {name}',
    greetingDay: 'Dobrý deň, {name}',
    greetingEvening: 'Dobrý večer, {name}',
    /** Fallback greeting when no name is known yet. */
    greetingNeutral: 'Vitajte doma',
    add: 'Pridať',
    /** Section titles on the at-a-glance dashboard. */
    todayTitle: 'Dnes',
    childrenTitle: 'Deti',
    shoppingTitle: 'Nákupy',
    expensesTitle: 'Náklady domácnosti',
    lastNoteTitle: 'Posledný odkaz',
    /** Small links/summaries under sections. */
    tomorrow: 'Zajtra',
    tomorrowEvents: 'Zajtra: {count} udalosti',
    viewAll: 'Zobraziť všetko',
    nothingToday: 'Dnes nič v kalendári.',
    noChildrenYet: 'Zatiaľ tu nie je žiadne dieťa.',
    noShoppingYet: 'Žiadny nákupný zoznam.',
    noExpensesYet: 'Tento mesiac zatiaľ žiadne náklady.',
    noNotesYet: 'Zatiaľ žiadne odkazy.',
    thisMonth: 'Tento mesiac',
    /** Expenses summary line, e.g. "3 nevyrovnané". */
    outstandingCount: '{count} nevyrovnané',
    /** Empty state — brand-new family with nothing added yet. */
    emptyTitle: 'Vitajte doma 👋',
    emptyLead: 'Začnite tromi krokmi a máte rodinu na jednom mieste.',
    emptyAddChild: 'Pridajte prvé dieťa',
    emptyInvitePartner: 'Pozvite partnera',
    emptyCreateList: 'Vytvorte nákupný zoznam',
  },

  children: {
    title: 'Deti',
    add: 'Pridať dieťa',
    addTitle: 'Pridať dieťa',
    none: 'Zatiaľ tu nie je žiadne dieťa.',
    noneLead: 'Pridajte prvé dieťa — narodeniny, zdravie aj kontakty budete mať poruke.',
    nameLabel: 'Meno',
    namePlaceholder: 'napr. Ema',
    emojiLabel: 'Avatar (emoji)',
    birthDateLabel: 'Dátum narodenia',
    ageYears: 'rokov',
    selectPrompt: 'Vyberte dieťa hore.',
    /** Kid-profile destination: labels for the per-child quick links. */
    profileTitle: 'Profil dieťaťa',
    profileLead: 'Všetko o dieťati na jednom mieste — jedným klikom.',
    quickHealth: 'Zdravie',
    quickContacts: 'Kontakty',
    quickCalendar: 'Kalendár',
    quickDocuments: 'Dokumenty',
    birthday: 'Narodeniny',
    ageLabel: 'Vek',
    noBirthDate: 'Dátum narodenia nie je vyplnený.',
    editTitle: 'Upraviť dieťa',
    removeConfirm: 'Naozaj odstrániť toto dieťa? Jeho záznamy ostanú zachované.',
  },

  contacts: {
    title: 'Kontakty',
    add: 'Pridať kontakt',
    empty: 'Zatiaľ žiadne kontakty.',
    typeLabel: 'Typ',
    labelLabel: 'Názov',
    labelPlaceholder: 'napr. MUDr. Nováková',
    phoneLabel: 'Telefón',
    addressLabel: 'Adresa',
    types: {
      doctor: 'Lekár',
      school: 'Škola',
      dentist: 'Zubár',
      activity: 'Krúžok',
      other: 'Iné',
    },
  },

  health: {
    title: 'Zdravotný záznam',
    sensitive: 'Citlivé údaje (GDPR čl. 9)',
    lead: 'Dôležité zdravotné informácie na jednom mieste — pre prípad núdze.',
    allergies: 'Alergie',
    medications: 'Lieky',
    insurance: 'Poisťovňa',
    bloodType: 'Krvná skupina',
    conditions: 'Diagnózy / stavy',
    notes: 'Poznámky',
    updatedAt: 'Naposledy upravené',
    placeholderNone: 'Nevyplnené',
  },

  calendar: {
    title: 'Kalendár',
    add: 'Pridať udalosť',
    empty: 'Zatiaľ žiadne udalosti.',
    upcoming: 'Najbližšie',
    titleLabel: 'Názov udalosti',
    typeLabel: 'Typ',
    startLabel: 'Začiatok',
    endLabel: 'Koniec',
    allDayLabel: 'Celý deň',
    locationLabel: 'Miesto',
    /** Separated-mode only: the custody sub-view tab label. */
    custodyView: 'Striedavá starostlivosť',
    /**
     * Event-type labels. `types` is the family-mode default (no handover/custody
     * surfaced). `typesSeparated` adds the co-parenting types and is selected
     * when `family.mode === 'separated'`. Both carry every key so callers can
     * index either map by the same `CalendarEventType`.
     */
    types: {
      appointment: 'Termín',
      handover: 'Iné',
      custody: 'Iné',
      school: 'Škola',
      activity: 'Aktivita',
      other: 'Iné',
    },
    typesSeparated: {
      appointment: 'Termín',
      handover: 'Odovzdávka',
      custody: 'Striedanie',
      school: 'Škola',
      activity: 'Aktivita',
      other: 'Iné',
    },
  },

  custody: {
    title: 'Rozvrh striedania',
    none: 'Rozvrh striedania zatiaľ nie je nastavený.',
    patternLabel: 'Vzor',
    startLabel: 'Začiatok rozvrhu',
    notesLabel: 'Poznámky',
    set: 'Nastaviť rozvrh',
    update: 'Upraviť rozvrh',
    parentA: 'Rodič A',
    parentB: 'Rodič B',
    thisWeek: 'Tento týždeň',
    patterns: {
      'week-week': 'Týždeň / týždeň',
      '2-2-3': '2-2-3',
      custom: 'Vlastný',
    },
  },

  documents: {
    title: 'Dokumenty',
    sensitive: 'Citlivé',
    add: 'Pridať dokument',
    empty: 'Zatiaľ žiadne dokumenty.',
    nameLabel: 'Názov',
    categoryLabel: 'Kategória',
    fileLabel: 'Súbor',
    fileHint: 'Súbor sa v lokálnom režime neuploaduje — uloží sa len metadáta.',
    uploadedBy: 'Nahral',
    size: 'Veľkosť',
  },

  shopping: {
    title: 'Nákupy',
    /** List picker + creation. */
    listLabel: 'Zoznam',
    newList: 'Nový zoznam',
    newListTitle: 'Nový nákupný zoznam',
    listNameLabel: 'Názov zoznamu',
    listNamePlaceholder: 'napr. Potraviny',
    renameList: 'Premenovať zoznam',
    deleteList: 'Vymazať zoznam',
    deleteListConfirm: 'Naozaj vymazať tento zoznam aj so všetkými položkami?',
    noLists: 'Zatiaľ žiadny zoznam.',
    noListsLead: 'Vytvorte prvý zoznam — napríklad „Potraviny“ alebo „Drogéria“.',
    /** Item add + list. */
    addItem: 'Pridať položku',
    addItemPlaceholder: 'Pridať položku…',
    itemNameLabel: 'Položka',
    empty: 'Zoznam je prázdny.',
    emptyLead: 'Pridajte prvú položku vyššie.',
    /** Category grouping (free-form label on each item). */
    categoryLabel: 'Kategória',
    categoryNone: 'Bez kategórie',
    categoryPlaceholder: 'napr. Ovocie a zelenina',
    /** Assignee = the member responsible for buying the item. */
    assigneeLabel: 'Kto kúpi',
    assigneeNone: 'Ktokoľvek',
    /** Buy-off. */
    bought: 'Kúpené',
    markBought: 'Označiť ako kúpené',
    clearBought: 'Vymazať kúpené',
    /** {count} → number of checked items to clear. */
    clearBoughtCount: 'Vymazať kúpené ({count})',
    clearBoughtConfirm: 'Vymazať všetky kúpené položky z tohto zoznamu?',
    itemCount: '{count} položiek',
  },

  messages: {
    /**
     * "Odkazy" in family mode (warm) / "Denník správ" in separated (court-log).
     * Bare `title`/`lead`/`empty` default to the family wording; the `*Family` and
     * `*Separated` variants let the mode-aware UI pick explicitly. Under the hood
     * messages stay append-only in BOTH modes.
     */
    title: 'Odkazy',
    titleFamily: 'Odkazy',
    titleSeparated: 'Denník správ',
    lead: 'Krátke rodinné odkazy a poznámky — napríklad „Vyzdvihni Emu o 15:00“.',
    leadFamily: 'Krátke rodinné odkazy a poznámky — napríklad „Vyzdvihni Emu o 15:00“.',
    leadSeparated:
      'Nemenný, súdne použiteľný záznam komunikácie. Správy sa nedajú upraviť ani vymazať.',
    placeholder: 'Napíšte odkaz…',
    placeholderSeparated: 'Napíšte správu…',
    send: 'Odoslať',
    empty: 'Zatiaľ žiadne odkazy. Napíšte prvý.',
    emptyFamily: 'Zatiaľ žiadne odkazy. Napíšte prvý.',
    emptySeparated: 'Denník je zatiaľ prázdny. Napíšte prvú správu.',
    newest: 'Najnovší',
    /** Shown only in separated mode (the append-only guarantee matters there). */
    appendOnlyNote: 'Append-only — bez úprav a mazania.',
  },

  expenses: {
    /**
     * "Náklady domácnosti" (household budgeting) in family mode vs "Náklady"
     * with split/settle machinery in separated. Bare keys default to family
     * wording; `*Separated` variants carry the co-parenting framing.
     */
    title: 'Náklady domácnosti',
    titleFamily: 'Náklady domácnosti',
    titleSeparated: 'Náklady',
    lead: 'Prehľad o tom, kto čo zaplatil — spoločný rozpočet domácnosti.',
    leadSeparated: 'Spoločné náklady na deti a ich rozdelenie medzi rodičov.',
    add: 'Pridať náklad',
    empty: 'Zatiaľ žiadne náklady.',
    amountLabel: 'Suma',
    categoryLabel: 'Kategória',
    descriptionLabel: 'Popis',
    childLabel: 'Dieťa',
    childAll: 'Celá rodina',
    incurredOnLabel: 'Dátum',
    /** Split UI is hidden in family mode; these labels surface in separated. */
    splitLabel: 'Delenie',
    split5050: 'Na polovicu (50/50)',
    splitCustom: 'Vlastné',
    payerShare: 'Váš podiel',
    otherShare: 'Podiel druhého rodiča',
    /** Family mode shows only "kto zaplatil" without any split. */
    paidBy: 'Kto zaplatil',
    paidByFamily: 'Kto zaplatil',
    settled: 'Vyrovnané',
    outstanding: 'Nevyrovnané',
    settle: 'Označiť ako vyrovnané',
    totalOutstanding: 'Nevyrovnané spolu',
    /** Family-mode month total (no settlement concept). */
    totalMonth: 'Spolu tento mesiac',
    you: 'Vy',
  },

  agreements: {
    title: 'Dohody',
    lead:
      'Písomné dohody medzi rodičmi. Jeden rodič navrhne dohodu, druhý ju odsúhlasí alebo odmietne. Odsúhlasená dohoda je nemenný, súdne použiteľný záznam.',
    propose: 'Navrhnúť dohodu',
    proposeTitle: 'Navrhnúť dohodu',
    proposeLead:
      'Sformulujte dohodu jasne. Druhý rodič ju bude môcť odsúhlasiť alebo odmietnuť.',
    proposeNote:
      'Po odsúhlasení sa dohoda uzamkne a už sa nedá upraviť ani vymazať.',
    titleLabel: 'Názov dohody',
    titlePlaceholder: 'napr. Letné prázdniny 2026',
    bodyLabel: 'Znenie dohody',
    bodyPlaceholder: 'Opíšte, na čom ste sa dohodli…',
    empty: 'Zatiaľ žiadne dohody.',
    emptyLead: 'Navrhnite prvú dohodu druhému rodičovi.',
    proposedBy: 'Navrhol/a',
    respondedBy: 'Vybavil/a',
    accept: 'Odsúhlasiť',
    decline: 'Odmietnuť',
    withdraw: 'Stiahnuť',
    locked: 'Uzamknuté — bez ďalších zmien.',
    actionStale: 'Dohoda už bola medzitým vybavená. Zoznam sme obnovili.',
    you: 'Vy',
    status: {
      proposed: 'Navrhnuté',
      accepted: 'Odsúhlasené',
      declined: 'Odmietnuté',
      withdrawn: 'Stiahnuté',
    },
  },

  sharing: {
    title: 'Rodina',
    lead:
      'Pozrite si, kto patrí do rodiny, a rozhodnite, čo každý vidí. Babku či dedka môžete pozvať ako dôveryhodnú osobu — uvidia len to, čo im dovolíte.',
    familyNameLabel: 'Názov rodiny',
    rename: 'Premenovať',
    renameTitle: 'Premenovať rodinu',
    members: 'Členovia rodiny',
    invite: 'Pozvať',
    inviteMember: 'Pozvať člena',
    inviteTitle: 'Pozvať člena rodiny',
    emailLabel: 'E-mail',
    roleLabel: 'Rola v rodine',
    roleParent: 'Rodič',
    roleParentDesc: 'Plný prístup ku všetkému — pre druhého rodiča.',
    roleTrusted: 'Dôveryhodná osoba',
    roleTrustedDesc:
      'Napríklad babka či dedko — vidia len to, čo zaškrtnete.',
    scopesLabel: 'Čo môže vidieť',
    scopesHint:
      'Rodič vidí všetko. Dôveryhodná osoba vidí len zaškrtnuté oblasti.',
    parentScopesNote: 'Rodič má automaticky plný prístup ku všetkým oblastiam.',
    sensitiveNote: 'Citlivé — udeľuje sa len výslovne.',
    seesLabel: 'Vidí',
    seesNothing: 'Zatiaľ nemá prístup k žiadnej oblasti.',
    fullAccess: 'Plný prístup',
    status: {
      invited: 'Pozvaný/á',
      active: 'Aktívny/a',
    },
    you: 'Vy',
    parentBadge: 'Rodič',
    trustedBadge: 'Dôveryhodná osoba',
    changeScopes: 'Upraviť prístup',
    removeMember: 'Odstrániť',
    removeConfirm: 'Naozaj odstrániť tohto člena z rodiny?',
    onlyParents: 'Členov rodiny a ich prístup môžu spravovať len rodičia.',
  },

  scopes: {
    children: 'Deti',
    calendar: 'Kalendár',
    contacts: 'Kontakty',
    health: 'Zdravie',
    documents: 'Dokumenty',
    messages: 'Odkazy',
    expenses: 'Náklady',
    shopping: 'Nákupy',
  },

  mode: {
    /** Settings → "Režim rodiny" card. Parent-only, lives at the bottom. */
    cardTitle: 'Režim rodiny',
    cardLead:
      'Solandio je nastavené pre rodinu, ktorá žije spolu. Ak žijete v samostatných domácnostiach, môžete prepnúť režim — všetky vaše dáta a celá história detí zostávajú nedotknuté.',
    /** Current-mode labels. */
    familyLabel: 'Spoločná domácnosť',
    familyDesc: 'Kalendár, deti, nákupy a náklady pre celú rodinu.',
    separatedLabel: 'Oddelené domácnosti',
    separatedDesc:
      'Pridá striedavú starostlivosť, delenie nákladov, dohody a denník správ.',
    currentLabel: 'Aktuálny režim',
    /** Switch actions. */
    switchToSeparated: 'Prepnúť na oddelené domácnosti',
    switchToFamily: 'Prepnúť späť na spoločnú domácnosť',
    /** Two-step confirmation dialog (deliberate friction). */
    confirmToSeparatedTitle: 'Prepnúť do režimu pre oddelené domácnosti?',
    confirmToSeparatedLead:
      'Pridáme nástroje pre rodičov, ktorí žijú oddelene — striedavú starostlivosť, delenie nákladov, dohody a denník správ.',
    confirmToSeparatedNote:
      'Všetky vaše dáta a celá história detí zostávajú nedotknuté. Režim môžete kedykoľvek prepnúť späť.',
    confirmToSeparatedAction: 'Áno, prepnúť',
    confirmToFamilyTitle: 'Prepnúť späť na spoločnú domácnosť?',
    confirmToFamilyLead:
      'Nástroje pre oddelené domácnosti sa skryjú. Záznamy ostávajú uložené — pri ďalšom prepnutí ich nájdete tak, ako boli.',
    confirmToFamilyAction: 'Áno, prepnúť späť',
    /** Shown while the change is being saved + after it lands. */
    switching: 'Prepínam režim…',
    switched: 'Režim sa zmenil.',
    /** Gate copy when a non-parent somehow reaches the card. */
    onlyParents: 'Režim rodiny môžu meniť len rodičia.',
  },

  audit: {
    title: 'Audit',
    lead: 'Kto, čo a kedy zmenil. Záznam je nemenný (append-only).',
    empty: 'Zatiaľ žiadne záznamy.',
    onlyParents: 'Audit môžu vidieť len rodičia.',
  },

  errors: {
    forbidden: 'Na túto oblasť nemáte oprávnenie.',
    notFound: 'Položka sa nenašla.',
    generic: 'Akcia zlyhala. Skúste to znova.',
  },

  tour: {
    /** Re-invoke action (Settings + help button in the top bar). */
    start: 'Spustiť sprievodcu',
    help: 'Sprievodca',
    helpHint: 'Prejdite si rýchle predstavenie aplikácie kedykoľvek znova.',
    /** Joyride control labels. */
    back: 'Späť',
    next: 'Ďalej',
    last: 'Hotovo',
    skip: 'Preskočiť',
    close: 'Zavrieť',
    /** "Krok 2 z 6" — {step} a {steps} doplní Joyride. */
    progress: 'Krok {step} z {steps}',
    steps: {
      welcomeTitle: 'Vitajte v Solandio',
      welcome: 'Ukážeme vám to za pár sekúnd.',
      childrenTitle: 'Deti',
      children: 'Tu pridáte a prepínate deti.',
      modulesTitle: 'Všetko o rodine',
      modules:
        'Vľavo nájdete všetko pre rodinu — kalendár, deti, zdravie, nákupy, náklady, dokumenty a odkazy.',
      familyTitle: 'Rodina',
      family:
        'Tu pozvete partnera alebo babku/dedka a nastavíte, čo vidia.',
      settingsTitle: 'Nastavenia',
      settings: 'V Nastaveniach si zmeníte meno a jazyk.',
      finishTitle: 'Hotovo!',
      finish: 'Začnite pridaním dieťaťa alebo nákupného zoznamu.',
    },
  },
} as const;

/**
 * Widen the `as const` literal types of the source dictionary to their base
 * types (e.g. the specific Slovak string → `string`), so translated locale
 * files (en/de/cs/pl/hu/fr) satisfy the `Dictionary` shape. Readonly arrays are
 * preserved so `as const` locale files remain assignable.
 */
type Widen<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T extends readonly (infer U)[]
        ? readonly Widen<U>[]
        : { readonly [K in keyof T]: Widen<T[K]> };

export type Dictionary = Widen<typeof sk>;
